import React, { useEffect, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { t } from "i18next";
import { useFetch } from "../../../../hooks/useFetch";
import { Division } from "../../../../types/types";
import { Label } from "../../../../ui/Label/Label";
import { Input } from "../../../../ui/Input/Input";
import { useAppStore } from "../../../../stores/RootStore";
import { SelectButton } from "../../../../ui/SelectButton/SelectButton";
import DeleteDivision from "../../DeleteDivision";
import { ScrollArea } from "../../../../ui/ScrollArea/ScrollArea";
import { Button } from "../../../../ui/Button";
import { Separator } from "../../../../ui/Separator";

type Props = {
  id: string | undefined;
  currentDivision: Division | undefined;
};

const DivisionSettingsTab = ({ id, currentDivision }: Props) => {
  let [isDeleteOpen, setIsDeleteOpen] = useState(false);

  const divisions = useAppStore((store) => store.divisions);
  const divisionsList = divisions?.map((division) => {
    return { value: division.id, label: division.attributes.name };
  })!;

  const { register, reset, handleSubmit, control, getValues } = useForm({
    defaultValues: {
      name: currentDivision?.attributes.name,
      cipher: currentDivision?.attributes.cipher,
      order_number: currentDivision?.attributes.order_number,
      parent: currentDivision?.attributes.parent_id
        ? divisionsList.find((division) => division.value == String(currentDivision?.attributes.parent_id))
        : { value: "", label: "" },
    },
  });

  function openDeleteModal() {
    setIsDeleteOpen(true);
  }

  function closeDeleteModal() {
    setIsDeleteOpen(false);
  }

  const [{ response: itemUpdateResponse, isLoading: itemUpdateIsLoading, error: itemUpdateError }, doFetchItemUpdate] =
    useFetch(`/directories/divisions/${id}`);

  const setDivisions = useAppStore((store) => store.setDivisions);

  const editDivisionItem = (itemSaveResponse: { data: unknown }) => {
    const newCurrentItemData = itemSaveResponse.data as unknown as Division;
    const withoutCurrentItem = divisions?.filter((division) => division.id != currentDivision?.id)!;
    setDivisions([...withoutCurrentItem, newCurrentItemData]);
  };

  useEffect(() => {
    if (!itemUpdateResponse) return;
    editDivisionItem(itemUpdateResponse);
  }, [itemUpdateResponse]);

  const onSubmit = (formData: any) => {
    doFetchItemUpdate({
      method: "PUT",
      data: {
        data: {
          name: formData.name,
          cipher: formData.cipher,
          order_number: formData.order_number,
          parent_id: formData.parent.value ? formData.parent.value : "",
        },
      },
    });
  };

  return (
    <>
      <div className="px-6">
        <form onSubmit={handleSubmit(onSubmit)}>
          <ScrollArea>
            <div className="flex flex-col gap-3">
              <Controller
                name="name"
                control={control}
                rules={{ required: true }}
                render={({ field }) => (
                  <div className="relative space-y-2">
                    <Label>{`${t("division_name")}*`}</Label>
                    <Input {...field} />
                  </div>
                )}
              />
              <Controller
                name="cipher"
                control={control}
                render={({ field }) => (
                  <div className="relative space-y-2">
                    <Label>{t("cipher")}</Label>
                    <Input {...field} />
                  </div>
                )}
              />

              <Controller
                name="parent"
                control={control}
                render={({ field: { value, onChange } }) => (
                  <div className="relative space-y-2">
                    <Label>{t("parent_division")}</Label>
                    <SelectButton
                      label={t("choose_division")}
                      searchable={true}
                      options={divisionsList}
                      value={value ? divisionsList.find((s) => s.value == value.value)! : null}
                      onChange={onChange}
                    />
                  </div>
                )}
              />
              <Controller
                name="order_number"
                control={control}
                render={({ field }) => (
                  <div className="relative space-y-2">
                    <Label>{t("division_order_number")}</Label>
                    <Input {...field} />
                  </div>
                )}
              />
              <div className="mt-8 flex w-full justify-between">
                <Button variant={"destructive"} size={"sm"} type="button" onClick={() => openDeleteModal()}>
                  {t("delete")}
                </Button>
                <Button variant={"default"} size={"sm"} type="submit">
                  {t("save")}
                </Button>
              </div>
            </div>
          </ScrollArea>
        </form>
      </div>
      <DeleteDivision id={id!} isDeleteOpen={isDeleteOpen} closeModal={closeDeleteModal} />
    </>
  );
};

export default DivisionSettingsTab;
